import React, { useState, useEffect } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Card,
  CardContent,
  CardMedia,
  InputAdornment,
  FormHelperText,
  Alert,
  Paper,
  CircularProgress
} from '@mui/material';
import { CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const steps = ['Informações Básicas', 'Segmentação', 'Orçamento e Programação', 'Criativos', 'Revisão'];

const initialFormData = {
  name: '',
  objective: '',
  description: '',
  ageMin: '18',
  ageMax: '65',
  gender: 'all',
  locations: [],
  interests: [],
  budgetType: 'daily',
  budget: '',
  startDate: '',
  endDate: '',
  selectedCreatives: []
};

const objectives = [
  { value: 'awareness', label: 'Reconhecimento de Marca' },
  { value: 'traffic', label: 'Tráfego' },
  { value: 'engagement', label: 'Engajamento' },
  { value: 'leads', label: 'Geração de Leads' },
  { value: 'conversions', label: 'Conversões' },
  { value: 'sales', label: 'Vendas' }
];

const genderOptions = [
  { value: 'all', label: 'Todos' },
  { value: 'male', label: 'Masculino' },
  { value: 'female', label: 'Feminino' }
];

const popularInterests = [
  'Tecnologia', 'Moda', 'Esportes', 'Viagens', 'Gastronomia',
  'Fitness', 'Música', 'Cinema', 'Games', 'Negócios',
  'Educação', 'Saúde', 'Beleza', 'Automóveis', 'Pets'
];

function CampaignForm() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [newLocation, setNewLocation] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [creatives, setCreatives] = useState([]);
  const [loadingCreatives, setLoadingCreatives] = useState(false);

  // Carregar criativos quando chegar no step 3
  useEffect(() => {
    if (activeStep === 3) {
      fetchCreatives();
    }
  }, [activeStep]);

  const fetchCreatives = async () => {
    console.log('=== CARREGANDO CRIATIVOS NO STEP 3 ===');
    setLoadingCreatives(true);
    
    try {
      const token = localStorage.getItem('token');
      console.log('Token disponível:', !!token);
      
      if (!token) {
        console.warn('⚠️ Token não encontrado');
        setCreatives([]);
        return;
      }

      console.log('📡 Fazendo requisição para /api/creatives...');
      const response = await fetch('https://growly-test-production.up.railway.app/api/creatives', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('📊 Status da resposta:', response.status, response.statusText);

      if (!response.ok) {
        throw new Error(`Erro ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('📦 Dados recebidos:', data);
      console.log('📦 Tipo de dados:', Array.isArray(data) ? 'Array' : typeof data);
      
      // Aceitar diferentes formatos de resposta
      let creativesArray = [];
      
      if (Array.isArray(data)) {
        creativesArray = data;
        console.log('✅ Dados já são um array');
      } else if (data.data && Array.isArray(data.data)) {
        creativesArray = data.data;
        console.log('✅ Dados em data.data');
      } else if (data.creatives && Array.isArray(data.creatives)) {
        creativesArray = data.creatives;
        console.log('✅ Dados em data.creatives');
      } else {
        console.error('❌ Formato de resposta não reconhecido:', data);
        creativesArray = [];
      }
      
      setCreatives(creativesArray);
      console.log('✅ Criativos setados no state:', creativesArray.length);
      
      if (creativesArray.length > 0) {
        console.log('📸 Primeiro criativo:', creativesArray[0]);
      }
      
    } catch (error) {
      console.error('❌ Erro completo ao carregar criativos:', error);
      console.error('Stack trace:', error.stack);
      setCreatives([]);
    } finally {
      setLoadingCreatives(false);
      console.log('=== FIM DO CARREGAMENTO ===');
    }
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 0) {
      if (!formData.name.trim()) newErrors.name = 'Nome é obrigatório';
      if (!formData.objective) newErrors.objective = 'Objetivo é obrigatório';
    }

    if (step === 1) {
      if (parseInt(formData.ageMin) > parseInt(formData.ageMax)) {
        newErrors.ageMin = 'Idade mínima deve ser menor que máxima';
      }
      if (formData.locations.length === 0) {
        newErrors.locations = 'Adicione pelo menos uma localização';
      }
    }

    if (step === 2) {
      if (!formData.budget || parseFloat(formData.budget) <= 0) {
        newErrors.budget = 'Orçamento deve ser maior que zero';
      }
      if (!formData.startDate) newErrors.startDate = 'Data de início é obrigatória';
      if (!formData.endDate) newErrors.endDate = 'Data de término é obrigatória';
      if (formData.startDate && formData.endDate && formData.startDate >= formData.endDate) {
        newErrors.endDate = 'Data de término deve ser posterior à data de início';
      }
    }

    if (step === 3) {
      if (formData.selectedCreatives.length === 0) {
        newErrors.selectedCreatives = 'Selecione pelo menos um criativo';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddLocation = () => {
    if (newLocation.trim() && !formData.locations.includes(newLocation.trim())) {
      handleChange('locations', [...formData.locations, newLocation.trim()]);
      setNewLocation('');
    }
  };

  const handleRemoveLocation = (location) => {
    handleChange('locations', formData.locations.filter((l) => l !== location));
  };

  const handleAddInterest = (interest) => {
    if (!formData.interests.includes(interest)) {
      handleChange('interests', [...formData.interests, interest]);
    }
  };

  const handleRemoveInterest = (interest) => {
    handleChange('interests', formData.interests.filter((i) => i !== interest));
  };

  const handleToggleCreative = (creativeId) => {
    const isSelected = formData.selectedCreatives.includes(creativeId);
    if (isSelected) {
      handleChange('selectedCreatives', formData.selectedCreatives.filter((id) => id !== creativeId));
    } else {
      handleChange('selectedCreatives', [...formData.selectedCreatives, creativeId]);
    }
    // Limpar erro ao selecionar um criativo
    if (errors.selectedCreatives) {
      setErrors((prev) => ({ ...prev, selectedCreatives: '' }));
    }
  };

  const calculateEstimatedReach = () => {
    const baseBudget = parseFloat(formData.budget);
    const costPerReach = 0.05;
    const duration = Math.ceil((new Date(formData.endDate) - new Date(formData.startDate)) / (1000 * 60 * 60 * 24));
    
    let totalBudget = baseBudget;
    if (formData.budgetType === 'daily') {
      totalBudget = baseBudget * duration;
    }
    
    const reach = Math.floor(totalBudget / costPerReach);
    const min = Math.floor(reach * 0.8);
    const max = Math.floor(reach * 1.2);
    return `${min.toLocaleString('pt-BR')} - ${max.toLocaleString('pt-BR')}`;
  };

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      const campaignData = {
        id: 'CAMP-' + Date.now(),
        name: formData.name,
        objective: objectives.find(o => o.value === formData.objective)?.label || formData.objective,
        status: 'Ativa',
        budget: parseFloat(formData.budget),
        budgetType: formData.budgetType,
        startDate: formData.startDate,
        endDate: formData.endDate,
        locations: formData.locations,
        interests: formData.interests,
        creativesCount: formData.selectedCreatives.length,
        estimatedReach: calculateEstimatedReach()
      };

      try {
        const token = localStorage.getItem('token');
        
        const campaignPayload = {
          name: formData.name,
          objective: formData.objective,
          description: formData.description,
          ageMin: parseInt(formData.ageMin),
          ageMax: parseInt(formData.ageMax),
          gender: formData.gender,
          locations: formData.locations,
          interests: formData.interests,
          budgetType: formData.budgetType,
          budget: parseFloat(formData.budget),
          startDate: formData.startDate,
          endDate: formData.endDate,
          creativeIds: formData.selectedCreatives
        };

        const response = await fetch('https://growly-test-production.up.railway.app/api/campaigns', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(campaignPayload)
        });

        if (response.ok) {
          const result = await response.json();
          if (result.data?.id || result.id) {
            campaignData.id = result.data?.id || result.id;
          }
        } else {
          console.warn('API não disponível, prosseguindo com dados locais');
        }
      } catch (apiError) {
        console.warn('Erro ao conectar com API, prosseguindo com dados locais:', apiError);
      }

      await new Promise(resolve => setTimeout(resolve, 1000));

      navigate('/campaign-success', { 
        state: { campaignData },
        replace: true 
      });

    } catch (error) {
      console.error('Erro ao criar campanha:', error);
      alert(`Erro ao criar campanha: ${error.message}\nPor favor, tente novamente.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Informações Básicas da Campanha
            </Typography>
            
            <TextField
              fullWidth
              label="Nome da Campanha"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
              sx={{ mb: 3 }}
              required
            />

            <FormControl fullWidth sx={{ mb: 3 }} error={!!errors.objective} required>
              <InputLabel>Objetivo da Campanha</InputLabel>
              <Select
                value={formData.objective}
                onChange={(e) => handleChange('objective', e.target.value)}
                label="Objetivo da Campanha"
              >
                {objectives.map((obj) => (
                  <MenuItem key={obj.value} value={obj.value}>
                    {obj.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.objective && <FormHelperText>{errors.objective}</FormHelperText>}
            </FormControl>

            <TextField
              fullWidth
              label="Descrição"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              multiline
              rows={4}
              helperText="Descreva os objetivos e detalhes da campanha"
            />
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Segmentação de Público
            </Typography>

            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Idade Mínima"
                  value={formData.ageMin}
                  onChange={(e) => handleChange('ageMin', e.target.value)}
                  error={!!errors.ageMin}
                  helperText={errors.ageMin}
                  inputProps={{ min: 13, max: 65 }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Idade Máxima"
                  value={formData.ageMax}
                  onChange={(e) => handleChange('ageMax', e.target.value)}
                  inputProps={{ min: 13, max: 65 }}
                />
              </Grid>
            </Grid>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Gênero</InputLabel>
              <Select
                value={formData.gender}
                onChange={(e) => handleChange('gender', e.target.value)}
                label="Gênero"
              >
                {genderOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Localizações *
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Ex: São Paulo, Brasil"
                  value={newLocation}
                  onChange={(e) => setNewLocation(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddLocation()}
                  error={!!errors.locations}
                />
                <Button variant="outlined" onClick={handleAddLocation}>
                  Adicionar
                </Button>
              </Box>
              {errors.locations && (
                <Typography color="error" variant="caption">
                  {errors.locations}
                </Typography>
              )}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {formData.locations.map((location) => (
                  <Chip
                    key={location}
                    label={location}
                    onDelete={() => handleRemoveLocation(location)}
                  />
                ))}
              </Box>
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Interesses
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {popularInterests.map((interest) => (
                  <Chip
                    key={interest}
                    label={interest}
                    onClick={() => handleAddInterest(interest)}
                    color={formData.interests.includes(interest) ? 'primary' : 'default'}
                    variant={formData.interests.includes(interest) ? 'filled' : 'outlined'}
                  />
                ))}
              </Box>
              {formData.interests.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="textSecondary">
                    Selecionados:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {formData.interests.map((interest) => (
                      <Chip
                        key={interest}
                        label={interest}
                        onDelete={() => handleRemoveInterest(interest)}
                        color="primary"
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Orçamento e Programação
            </Typography>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Tipo de Orçamento</InputLabel>
              <Select
                value={formData.budgetType}
                onChange={(e) => handleChange('budgetType', e.target.value)}
                label="Tipo de Orçamento"
              >
                <MenuItem value="daily">Diário</MenuItem>
                <MenuItem value="total">Total da Campanha</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              type="number"
              label={`Orçamento ${formData.budgetType === 'daily' ? 'Diário' : 'Total'}`}
              value={formData.budget}
              onChange={(e) => handleChange('budget', e.target.value)}
              error={!!errors.budget}
              helperText={errors.budget}
              InputProps={{
                startAdornment: <InputAdornment position="start">R$</InputAdornment>,
              }}
              sx={{ mb: 3 }}
              required
            />

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Data de Início"
                  value={formData.startDate}
                  onChange={(e) => handleChange('startDate', e.target.value)}
                  error={!!errors.startDate}
                  helperText={errors.startDate}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: new Date().toISOString().split('T')[0] }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Data de Término"
                  value={formData.endDate}
                  onChange={(e) => handleChange('endDate', e.target.value)}
                  error={!!errors.endDate}
                  helperText={errors.endDate}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: formData.startDate || new Date().toISOString().split('T')[0] }}
                  required
                />
              </Grid>
            </Grid>

            {formData.budget && formData.startDate && formData.endDate && (
              <Alert severity="info" sx={{ mt: 3 }}>
                <Typography variant="body2">
                  <strong>Estimativa:</strong>
                  {formData.budgetType === 'daily' ? (
                    <> Orçamento total aproximado: R$ {(parseFloat(formData.budget) * Math.ceil((new Date(formData.endDate) - new Date(formData.startDate)) / (1000 * 60 * 60 * 24))).toFixed(2)}</>
                  ) : (
                    <> Orçamento diário aproximado: R$ {(parseFloat(formData.budget) / Math.ceil((new Date(formData.endDate) - new Date(formData.startDate)) / (1000 * 60 * 60 * 24))).toFixed(2)}</>
                  )}
                </Typography>
              </Alert>
            )}
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Selecione os Criativos
            </Typography>
            
            {errors.selectedCreatives && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errors.selectedCreatives}
              </Alert>
            )}

            {loadingCreatives ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : creatives.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Nenhum criativo disponível
                </Alert>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Faça upload de criativos antes de criar uma campanha
                </Typography>
                <Button 
                  variant="outlined" 
                  sx={{ mt: 2 }}
                  onClick={() => navigate('/upload')}
                >
                  Ir para Upload
                </Button>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {creatives.map((creative) => {
                  const isSelected = formData.selectedCreatives.includes(creative.id);
                  return (
                    <Grid item xs={12} sm={6} md={4} key={creative.id}>
                      <Card
                        sx={{
                          cursor: 'pointer',
                          border: isSelected ? '2px solid' : '2px solid transparent',
                          borderColor: isSelected ? 'primary.main' : 'transparent',
                          position: 'relative',
                          transition: 'all 0.3s',
                          '&:hover': {
                            transform: 'scale(1.02)',
                            boxShadow: 3
                          }
                        }}
                        onClick={() => handleToggleCreative(creative.id)}
                      >
                        {isSelected && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              zIndex: 1,
                              bgcolor: 'primary.main',
                              borderRadius: '50%',
                              p: 0.5
                            }}
                          >
                            <CheckCircle size={20} color="white" />
                          </Box>
                        )}
                        <CardMedia
                          component="img"
                          height="200"
                          image={creative.url || creative.file_url}
                          alt={creative.name || creative.file_name}
                          sx={{ objectFit: 'cover' }}
                          onError={(e) => {
                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23ddd" width="200" height="200"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EImagem indisponível%3C/text%3E%3C/svg%3E';
                          }}
                        />
                        <CardContent>
                          <Typography variant="body2" noWrap>
                            {creative.name || creative.file_name || 'Sem nome'}
                          </Typography>
                          {creative.created_at && (
                            <Typography variant="caption" color="textSecondary">
                              {new Date(creative.created_at).toLocaleDateString('pt-BR')}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Box>
        );

      case 4:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Revisão da Campanha
            </Typography>

            <Paper sx={{ p: 3, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Informações Básicas</strong>
              </Typography>
              <Typography variant="body2"><strong>Nome:</strong> {formData.name}</Typography>
              <Typography variant="body2">
                <strong>Objetivo:</strong> {objectives.find(o => o.value === formData.objective)?.label}
              </Typography>
              {formData.description && (
                <Typography variant="body2"><strong>Descrição:</strong> {formData.description}</Typography>
              )}
            </Paper>

            <Paper sx={{ p: 3, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Segmentação</strong>
              </Typography>
              <Typography variant="body2">
                <strong>Idade:</strong> {formData.ageMin} - {formData.ageMax} anos
              </Typography>
              <Typography variant="body2">
                <strong>Gênero:</strong> {genderOptions.find(g => g.value === formData.gender)?.label}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Localizações:</strong>
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {formData.locations.map(loc => (
                  <Chip key={loc} label={loc} size="small" />
                ))}
              </Box>
              {formData.interests.length > 0 && (
                <>
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    <strong>Interesses:</strong>
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {formData.interests.map(int => (
                      <Chip key={int} label={int} size="small" color="primary" />
                    ))}
                  </Box>
                </>
              )}
            </Paper>

            <Paper sx={{ p: 3, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Orçamento e Programação</strong>
              </Typography>
              <Typography variant="body2">
                <strong>Tipo:</strong> {formData.budgetType === 'daily' ? 'Diário' : 'Total'}
              </Typography>
              <Typography variant="body2">
                <strong>Orçamento:</strong> R$ {parseFloat(formData.budget).toFixed(2)}
              </Typography>
              <Typography variant="body2">
                <strong>Período:</strong> {new Date(formData.startDate).toLocaleDateString('pt-BR')} até {new Date(formData.endDate).toLocaleDateString('pt-BR')}
              </Typography>
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Criativos Selecionados ({formData.selectedCreatives.length})</strong>
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {creatives
                  .filter(c => formData.selectedCreatives.includes(c.id))
                  .map(creative => (
                    <Grid item xs={6} sm={4} md={3} key={creative.id}>
                      <Card>
                        <CardMedia
                          component="img"
                          height="120"
                          image={creative.url || creative.file_url}
                          alt={creative.name || creative.file_name}
                        />
                        <CardContent sx={{ p: 1 }}>
                          <Typography variant="caption" noWrap>
                            {creative.name || creative.file_name}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
              </Grid>
            </Paper>

            <Alert severity="success" sx={{ mt: 3 }}>
              <Typography variant="body2">
                Tudo pronto! Clique em "Criar Campanha" para finalizar.
              </Typography>
            </Alert>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        Nova Campanha
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Paper sx={{ p: 4, mb: 3 }}>
        {renderStepContent(activeStep)}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          disabled={activeStep === 0 || isSubmitting}
          onClick={handleBack}
          variant="outlined"
        >
          Voltar
        </Button>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleSubmit}
              size="large"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
            >
              {isSubmitting ? 'Criando...' : 'Criar Campanha'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              size="large"
            >
              Próximo
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default CampaignForm;